import { Button, Card, Input, Label, Select } from "@platform/ui";
import {
  draftOf,
  ErrorText,
  errorKey,
  issuesOf,
  issueText,
  LocalizedFields,
  localName,
  textOf,
  useSession,
  useText,
} from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslations } from "use-intl";
import { type OpsCategory, treeOrder } from "../lib/catalog";
import { locale } from "../lib/i18n";

type Kind = OpsCategory["listingKind"];
type Problem = { code?: string } | undefined;

/** Issues from a refusal, each shown in the operator's language under the form. */
function useRefusal() {
  const { t } = useText();
  const [messages, setMessages] = useState<readonly string[]>([]);
  const refuse = (problem: unknown, conflict?: () => void) => {
    if ((problem as Problem)?.code === "VERSION_CONFLICT") {
      setMessages([t("admin.categories.conflict")]);
      return conflict?.();
    }
    const issues = issuesOf(problem);
    setMessages(
      issues === undefined
        ? [t(errorKey(problem))]
        : Object.entries(issues).map(([path, code]) => `${path}: ${issueText(t, code) ?? code}`),
    );
  };
  return { messages, refuse, clear: () => setMessages([]) };
}

/** A new category: at the top level with its kind, or under a branch, which gives it its kind. */
function NewCategory({
  parent,
  onCreated,
  onCancel,
}: {
  parent: OpsCategory | null;
  onCreated: (category: OpsCategory) => void;
  onCancel: () => void;
}) {
  const t = useTranslations();
  const { session } = useSession();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState(draftOf(null));
  const [kind, setKind] = useState<Kind>("goods");
  const [isLeaf, setIsLeaf] = useState(parent !== null);
  const { messages, refuse, clear } = useRefusal();

  const create = async (event: FormEvent) => {
    event.preventDefault();
    clear();
    const { data, error } = await session.api.POST("/v1/ops/categories", {
      body: {
        parentId: parent?.id ?? null,
        slug: slug.trim(),
        name: textOf(name),
        ...(parent === null ? { listingKind: kind } : {}),
        isLeaf,
      },
    });
    if (data === undefined) return refuse(error);
    onCreated(data);
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={(event) => void create(event)}>
      <h2 className="text-subheading font-semibold text-ink">
        {parent === null
          ? t("admin.categories.newTop")
          : t("admin.categories.newUnder", { parent: localName(parent.name, locale) })}
      </h2>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="category-slug">{t("admin.categories.slug")}</Label>
        <Input
          id="category-slug"
          value={slug}
          maxLength={60}
          onChange={(event) => setSlug(event.target.value)}
        />
      </div>
      <LocalizedFields
        id="category-name"
        label={t("admin.categories.name")}
        value={name}
        onChange={setName}
        maxLength={120}
      />
      {parent === null && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="category-kind">{t("admin.categories.kind")}</Label>
          <Select id="category-kind" value={kind} onChange={(event) => setKind(event.target.value as Kind)}>
            <option value="goods">{t("catalog.kind.goods")}</option>
            <option value="service">{t("catalog.kind.service")}</option>
          </Select>
        </div>
      )}
      <label className="flex items-center gap-2 text-small">
        <input type="checkbox" checked={isLeaf} onChange={(event) => setIsLeaf(event.target.checked)} />
        {t("admin.categories.isLeaf")}
      </label>
      {messages.map((message) => (
        <ErrorText key={message}>{message}</ErrorText>
      ))}
      <div className="flex gap-2">
        <Button type="submit" disabled={slug.trim() === "" || name.en.trim() === ""}>
          {t("admin.categories.create")}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </form>
  );
}

/** Renaming, moving within its branch, reordering, retiring, and turning a category into a leaf or a branch. */
function EditCategory({
  category,
  categories,
  onSaved,
  onConflict,
}: {
  category: OpsCategory;
  categories: readonly OpsCategory[];
  onSaved: (category: OpsCategory) => void;
  onConflict: () => void;
}) {
  const t = useTranslations();
  const { session } = useSession();
  const [name, setName] = useState(draftOf(category.name));
  const [parentId, setParentId] = useState(category.parentId ?? "");
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [isLeaf, setIsLeaf] = useState(category.isLeaf);
  const [status, setStatus] = useState(category.status);
  const [saved, setSaved] = useState(false);
  const { messages, refuse, clear } = useRefusal();
  // A category moves within its branch's kind, under a branch that is not itself or below it.
  const below = new Set<string>([category.id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const each of categories)
      if (each.parentId !== null && below.has(each.parentId) && !below.has(each.id)) {
        below.add(each.id);
        grew = true;
      }
  }
  const parents = treeOrder(categories).filter(
    ({ category: each }) => !each.isLeaf && each.listingKind === category.listingKind && !below.has(each.id),
  );

  const save = async (event: FormEvent) => {
    event.preventDefault();
    clear();
    setSaved(false);
    const order = Number.parseInt(sortOrder, 10);
    const { data, error } = await session.api.PATCH("/v1/ops/categories/{categoryId}", {
      params: { path: { categoryId: category.id }, header: { "if-match": String(category.version) } },
      body: {
        name: textOf(name),
        parentId: parentId === "" ? null : parentId,
        sortOrder: Number.isNaN(order) ? category.sortOrder : order,
        isLeaf,
        status,
      },
    });
    if (data === undefined) return refuse(error, onConflict);
    setSaved(true);
    onSaved(data);
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={(event) => void save(event)}>
      <h2 className="text-subheading font-semibold text-ink">{localName(category.name, locale)}</h2>
      <p className="text-small text-ink-muted">
        {t("admin.categories.facts", {
          slug: category.slug,
          kind: t(`catalog.kind.${category.listingKind}`),
          products: category.productCount,
        })}
      </p>
      <LocalizedFields
        id="category-name"
        label={t("admin.categories.name")}
        value={name}
        onChange={setName}
        maxLength={120}
      />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="category-parent">{t("admin.categories.parent")}</Label>
        <Select id="category-parent" value={parentId} onChange={(event) => setParentId(event.target.value)}>
          <option value="">{t("admin.categories.topLevel")}</option>
          {parents.map(({ category: each, depth }) => (
            <option key={each.id} value={each.id}>
              {`${"— ".repeat(depth)}${localName(each.name, locale)}`}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="category-order">{t("admin.categories.sortOrder")}</Label>
        <Input
          id="category-order"
          inputMode="numeric"
          className="w-32"
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value.replace(/\D/g, ""))}
        />
      </div>
      <label className="flex items-center gap-2 text-small">
        <input type="checkbox" checked={isLeaf} onChange={(event) => setIsLeaf(event.target.checked)} />
        {t("admin.categories.isLeaf")}
      </label>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="category-status">{t("admin.categories.status")}</Label>
        <Select
          id="category-status"
          value={status}
          onChange={(event) => setStatus(event.target.value as OpsCategory["status"])}
        >
          <option value="active">{t("admin.categories.active")}</option>
          <option value="retired">{t("admin.categories.retired")}</option>
        </Select>
      </div>
      {messages.map((message) => (
        <ErrorText key={message}>{message}</ErrorText>
      ))}
      {saved && <p className="text-small text-tone-done-fg">{t("admin.categories.saved")}</p>}
      <Button type="submit" className="self-start">
        {t("admin.categories.save")}
      </Button>
    </form>
  );
}

/**
 * The category tree (CAT-2, ADM-4, card 044): add, rename in both languages, move, reorder, and retire; each leaf's
 * attribute schema is edited on its own page. Every change is audited by the API.
 */
export function CategoriesPage() {
  const t = useTranslations();
  const { session } = useSession();
  const [categories, setCategories] = useState<readonly OpsCategory[] | undefined>();
  const [selected, setSelected] = useState<string | undefined>();
  const [adding, setAdding] = useState<"top" | "child" | undefined>();
  const [formKey, setFormKey] = useState(0);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/categories");
    if (data === undefined) return setError(t(errorKey(problem)));
    setCategories(data.items);
    setFormKey((key) => key + 1);
  }, [session, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (categories === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const current = categories.find((each) => each.id === selected);
  const replaced = (next: OpsCategory) =>
    setCategories((all) =>
      all?.some((each) => each.id === next.id)
        ? all.map((each) => (each.id === next.id ? next : each))
        : [...(all ?? []), next],
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-title font-semibold text-ink">{t("admin.categories.title")}</h1>
        <Button size="sm" variant="secondary" onClick={() => setAdding("top")}>
          {t("admin.categories.addTop")}
        </Button>
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_24rem]">
        <Card className="p-2">
          <ul className="flex flex-col text-small">
            {treeOrder(categories).map(({ category, depth }) => (
              <li key={category.id}>
                <button
                  type="button"
                  className={`flex w-full flex-wrap items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted ${selected === category.id ? "bg-brand-50" : ""}`}
                  style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}
                  onClick={() => {
                    setSelected(category.id);
                    setAdding(undefined);
                  }}
                >
                  <span
                    className={category.status === "retired" ? "text-ink-muted line-through" : "font-medium"}
                  >
                    {localName(category.name, locale)}
                  </span>
                  <span className="text-caption text-ink-muted">{category.slug}</span>
                  {category.isLeaf && (
                    <span className="text-caption text-ink-muted">
                      {t("admin.categories.leafFacts", {
                        version: category.attributeSchemaVersion,
                        products: category.productCount,
                      })}
                    </span>
                  )}
                  {category.liveOnOlderSchema > 0 && (
                    <span className="rounded-full bg-brand-50 px-2 text-caption text-brand-700">
                      {t("admin.categories.olderSchema", { count: category.liveOnOlderSchema })}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Card>
        <Card className="flex h-fit flex-col gap-4">
          {adding !== undefined ? (
            <NewCategory
              parent={adding === "child" ? (current ?? null) : null}
              onCreated={(created) => {
                replaced(created);
                setAdding(undefined);
                setSelected(created.id);
              }}
              onCancel={() => setAdding(undefined)}
            />
          ) : current === undefined ? (
            <p className="text-small text-ink-muted">{t("admin.categories.choose")}</p>
          ) : (
            <>
              <EditCategory
                key={`${current.id}-${formKey}`}
                category={current}
                categories={categories}
                onSaved={replaced}
                onConflict={() => void load()}
              />
              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                {current.isLeaf ? (
                  <Link
                    className="text-small text-brand-600 hover:text-brand-700"
                    to={`/categories/${current.id}/schema`}
                  >
                    {t("admin.categories.editSchema")}
                  </Link>
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => setAdding("child")}>
                    {t("admin.categories.addChild")}
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
